import { useCallback, useEffect } from "react";
import tracking from "frontend/tracking";
import { useSetAtom } from "jotai";
import { sidePanelType, SidepanelType } from "state-atoms";
import { RESET } from "jotai/utils";
import PenSubMenu from "frontend/penSubMenu";
import { CursorType } from "./cursor-type";
import StickyNoteSubMenu from "frontend/stickyNoteSubMenu";
import CardStackSubMenu from "./submenus/card-stack-submenu";
import useModalDeepLink from "frontend/hooks/use-modal-deep-link";
import { ModalType } from "frontend/modals/modal-manager";
import Modal from "frontend/modal/modal";
import TemplateOrgchartImportModal from "frontend/modals/template-org-chart-import-modal";
import FrameSubMenu from "frontend/frameSubMenu";
import { TableSubMenu } from "frontend/tableSubMenu";
import useCanvasTemplates from "frontend/hooks/use-canvas-templates";
import { ShapeInfo } from "./shape-libraries/side-panel/virtualized-list/virtualized-row";
import ShapeSubmenu from "./shape-libraries/submenu/shape-submenu";
import { CimSubMenu } from "frontend/cim-sub-menu/cim-sub-menu";
import MondayImportItems from "frontend/canvas-designer-new/monday-import-items/monday-import-items";
import { MondayImportItemsContextProvider } from "frontend/canvas-designer-new/monday-import-items/monday-import-items-context";

export default function SubMenu({
  cursorType,
  setShowSubmenu,
  setSubmenuData,
  submenuData,
  setCursorType,
  refToCursor,
  isCimReorderEnabled = false,
}: {
  showSubmenu: any;
  cursorType: any;
  setShowSubmenu: any;
  setSubmenuData: any;
  submenuData: any;
  setCursorType: any;
  refToCursor: any;
  isCimReorderEnabled?: boolean;
}) {
  const setOpenedSidePanel = useSetAtom(sidePanelType);

  const { templates } = useCanvasTemplates();
  const { modalTypeFromUrl, setModalDeepLink } = useModalDeepLink();

  useEffect(() => {
    const actionPerformed = modalTypeFromUrl === ModalType.TemplateLibrary;
    if (cursorType === CursorType.template && !actionPerformed) {
      setModalDeepLink({ modalType: ModalType.TemplateLibrary });
    }
  }, [cursorType]);

  const handleChooseShape = useCallback(
    (elementType: string, elementData: Record<string, unknown>, elementInfo: ShapeInfo, closeSubmenu = true) => {
      setCursorType(CursorType.shape);
      const { library, group, item } = elementInfo;
      const libraryPath = `${library}:${group}:${item}`;
      setShowSubmenu(!closeSubmenu);
      setSubmenuData({
        ...submenuData,
        [elementType]: {
          ...submenuData[elementType],
          ...elementData,
          override: elementData?.override ?? {},
          libraryPath,
        },
      });
    },
    [setShowSubmenu, setSubmenuData, submenuData]
  );

  const handleChooseConnector = useCallback(
    (lineType: string, showArrows: boolean) => {
      setCursorType(CursorType.connector);
      const startArrow = showArrows ? submenuData.connector.startArrow : "none";
      let endArrow = showArrows ? submenuData.connector.endArrow : "none";
      if (showArrows && endArrow === "none") {
        endArrow = "filled-rounded-arrow";
      }

      setShowSubmenu(false);
      setSubmenuData({
        ...submenuData,
        connector: {
          ...submenuData.connector,
          lineType,
          startArrow,
          endArrow,
        },
      });
    },
    [setShowSubmenu, setSubmenuData, submenuData]
  );

  switch (cursorType) {
    // TODO: Add mindmap submenu
    // case CursorType.mindmap: {
    //   return <MindmapSubMenu onDismiss={() => setShowSubmenu(false)} relativeTo={refToCursor} />;
    // }
    case CursorType.draw: {
      return (
        <PenSubMenu
          onMenuItemClick={(menuItem) => {
            setShowSubmenu(false);
            setSubmenuData({ ...submenuData, pen: { color: menuItem.data } });
          }}
          relativeTo={refToCursor}
        />
      );
    }
    case CursorType.shape: {
      return (
        <ShapeSubmenu
          onMenuItemClick={handleChooseShape}
          onConnectorClick={handleChooseConnector}
          onExpand={() => {
            setShowSubmenu(false);
            setOpenedSidePanel(SidepanelType.shapes);
            setCursorType(CursorType.pointer);
            tracking.trackAnalyticsEvent("shape-side-panel-opened");
          }}
          relativeTo={refToCursor}
        />
      );
    }
    case CursorType.stickyNote: {
      return (
        <StickyNoteSubMenu
          onMenuItemClick={(menuItem) => {
            setShowSubmenu(false);
            setSubmenuData({
              ...submenuData,
              stickyNote: { ...submenuData.stickyNote, color: menuItem.data },
            });
          }}
          relativeTo={refToCursor}
        />
      );
    }
    case CursorType.frame: {
      return (
        <FrameSubMenu
          onMenuItemClick={(menuItem) => {
            setShowSubmenu(false);
            setSubmenuData({
              ...submenuData,
              frame: { ...submenuData.frame, width: menuItem.data.width ?? null, height: menuItem.data.height ?? null },
            });
          }}
          relativeTo={refToCursor}
        />
      );
    }
    case CursorType.cardStack:
    case CursorType.liveIntegration: {
      return isCimReorderEnabled ? null : (
        <CardStackSubMenu
          selectedType={cursorType}
          onTypeClicked={(type) => {
            setCursorType(type);
          }}
          relativeTo={refToCursor}
        />
      );
    }
    case CursorType.mindmapOrgChart: {
      const smartOrgChartTemplate = templates?.find((t) => t.id === process.env.MONDAY_ORGCHART_TEMPLATE_ID);
      const onDismiss = (template?: any) => {
        setShowSubmenu(false);
        if (!template) {
          setCursorType(CursorType.pointer);
          return;
        }
        setSubmenuData({
          ...submenuData,
          template: { ...smartOrgChartTemplate, ...template[0] },
        });
        setCursorType(CursorType.mindmapOrgChart);
        setModalDeepLink({ modalType: RESET });
      };
      return (
        <Modal dimBackground={true}>
          <TemplateOrgchartImportModal onDismiss={onDismiss} />
        </Modal>
      );
    }
    case CursorType.table: {
      return (
        <TableSubMenu
          onClick={(table) => {
            setShowSubmenu(false);
            setCursorType(CursorType.table);
            setSubmenuData({ ...submenuData, table });
          }}
          currentDefault={submenuData.table}
          relativeTo={refToCursor}
        />
      );
    }
    case CursorType.cimWidgets: {
      return (
        <CimSubMenu
          relativeTo={refToCursor}
          onDismiss={() => {
            setCursorType(CursorType.pointer);
            setShowSubmenu(false);
          }}
          onSelected={(selected) => {
            if (selected.type == "widget") {
              setCursorType(selected.widget);
            } else if (selected.type == "template") {
              // Check if it's an org chart template with data already embedded
              const isOrgChartWithData =
                selected.template.alias === "org-chart-integration" && selected.template.element;

              if (isOrgChartWithData) {
                // Org chart data is ready - set cursor to mindmapOrgChart for placement
                setCursorType(CursorType.mindmapOrgChart);
                setSubmenuData({ ...submenuData, template: selected.template });
                setShowSubmenu(false);
                setModalDeepLink({ modalType: RESET });
              } else {
                // Regular template
                setCursorType(CursorType.template);
                setSubmenuData({ ...submenuData, template: selected.template });
                setShowSubmenu(false);
                setModalDeepLink({ modalType: RESET });
              }
            } else if (selected.type == "liveIntegration") {
              setCursorType(CursorType.liveIntegration);
              setSubmenuData({ ...submenuData, liveIntegration: selected.data });
              setShowSubmenu(false);
              setModalDeepLink({ modalType: RESET });
            } else {
              console.error("Unhandled selection type", selected);
            }
          }}
        />
      );
    }
    case CursorType.mondayIntegration: {
      return (
        <MondayImportItemsContextProvider>
          <MondayImportItems
            relativeTo={refToCursor}
            onDismiss={() => {
              setCursorType(CursorType.pointer);
              setShowSubmenu(false);
            }}
          />
        </MondayImportItemsContextProvider>
      );
    }
    default: {
      return null;
    }
  }
}
